import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});


// New JS

jQuery(document).ready(function($) {
  const $spans = $('.subheading-item');
  let index = 0;

  function showNext() {
    // Hide all first
    $spans.removeClass('active').hide();

    // Show current span
    $spans.eq(index).show().addClass('active');

    // If not the last span, schedule the next one
    if (index < $spans.length - 1) {
      index++;
      setTimeout(showNext, 1000); // Change 1000 = 1s delay between slides
    }
  }

  // Start animation
  showNext();


  //

  $(function() {
  const $hero = $('section.hero-banner');

  // Step 1: AFTER slides in from right
  $hero.css({
    '--after-right': '0px',
    '--after-opacity': 1
  });

  setTimeout(() => {
    // Step 2: Rotate AFTER a little
    $hero.css('--after-rotate', '10deg');

    setTimeout(() => {
      // Step 3: AFTER slides back/hides
      $hero.css({
        '--after-right': '-500px',
        '--after-opacity': 0
      });

      setTimeout(() => {
        // Step 4: BEFORE slides in from left
        $hero.css({
          '--before-left': '0px',
          '--before-opacity': 1
        });

        setTimeout(() => {
          // Step 5: Rotate BEFORE a little
          $hero.css('--before-rotate', '-10deg');

          setTimeout(() => {
            // Step 6: Show BOTH together, reset rotations
            $hero.css({
              '--after-right': '0px',
              '--after-opacity': 1,
              '--after-rotate': '0deg',
              '--before-rotate': '0deg'
            });
          }, 1000);
        }, 600);
      }, 800);
    }, 600);
  }, 800);
});

// 

  



    // Inject dynamic background for all matching spans
  $('span.text-box').each(function() {
    $(this).append('<span class="bg-anim"></span>');
  });

  // One-time hover animation
  $(document).on('mouseenter', 'span.text-box', function() {
    const $el = $(this);
    const $bg = $el.find('.bg-anim');

    // Run only once per element
    if ($el.data('animated')) return;
    $el.data('animated', true);

    // Step 1: Fade in background
    $bg.stop().animate({ opacity: 1 }, 600, function() {
      // Step 2: Grow to full width
      $bg.animate({ width: '105%' }, 800);
    });

    // Step 3: Apply gradient text permanently
    $el.addClass('gradient-text');
  });


// 

function revealGifts() {
    var sectionTop = $('.gift-grid-wrapper').offset().top;
    var scrollTop = $(window).scrollTop();
    var windowHeight = $(window).height();

    // Trigger when section enters viewport
    if (scrollTop + windowHeight > sectionTop + 100) {
      $('.gift-card-item').each(function(i) {
        var item = $(this);
        setTimeout(function() {
          item.addClass('visible');
        }, i * 300); // 300ms delay between each
      });
      // Run only once
      $(window).off('scroll', revealGifts);
    }
  }

  $(window).on('scroll', revealGifts);
  revealGifts(); // in case it's already visible

});



jQuery(document).ready(function($) {
 function animateSection() {
    var $section = $('section.two-col-promo');
    var sectionTop = $section.offset().top;
    var scrollTop = $(window).scrollTop();
    var windowHeight = $(window).height();

    // trigger slightly before fully visible
    if (scrollTop + windowHeight > sectionTop + 100) {
      $section.addClass('visible');
    }
  }

  $(window).on('scroll', animateSection);
  animateSection(); // run once on load
});


        window.addEventListener('load', function() {
            const textElement = document.querySelector('.alet__text');
            const originalText = textElement.textContent;
            
            // Wrap each character in a span
            textElement.innerHTML = originalText.split('').map(char => {
                if (char === ' ') {
                    return '<span>&nbsp;</span>';
                }
                return '<span>' + char + '</span>';
            }).join('');

            // Animate each span sequentially
            const spans = textElement.querySelectorAll('span');
            spans.forEach((span, index) => {
                setTimeout(() => {
                    span.classList.add('animate');
                }, index * 30);
            });
        });
